import { resolveStoredFileUrl } from "utils/storedFileUrl";
// React
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";

// @mui material components
import {
  Card,
  CircularProgress,
  Collapse,
  Fade,
  Grid,
  Icon,
  InputLabel,
  Modal,
  Tooltip,
} from "@mui/material";

// Custom UI components
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import SoftInput from "components/SoftInput";
import { ConfirmarContext } from "context/ConfirmarContext";
import { formatDate } from "utils/formatters";
import { getUproColor } from "utils/colors";
import { formatMoneyPunto } from "utils/formatters";
import { recortarTexto } from "utils/formatters";
import DinamicModal from "components/Modals/DinamicModal";
import { nombreCompraDetalle } from "utils/presentacionCompra";
import dayjs from "dayjs";
import { UserContext } from "context/user";
import { PERMISOS_COMPRAS, tienePermisoCompras } from "layouts/compras/permisos";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "75%", md: "65%", xxl: "60%" },
  overflow: "auto",
  p: 3,
};

export default function ModalDetalleLicitacion({ open, handleClose, idPedidoCompra, refetchParent, mode }) {
  const { handleSnackbar } = useContext(MessageManager);
  const { confirmar } = useContext(ConfirmarContext);
  const { user, verificarAcceso } = useContext(UserContext);
  const puedeGestionar = mode !== "Lectura" && tienePermisoCompras(user, verificarAcceso, PERMISOS_COMPRAS.GESTIONAR_COTIZACION);
  const puedeVerOfertas = tienePermisoCompras(user, verificarAcceso, PERMISOS_COMPRAS.VER_OFERTAS);
  const puedePreadjudicar = mode !== "Lectura" && tienePermisoCompras(user, verificarAcceso, PERMISOS_COMPRAS.PREADJUDICAR);
  const puedeVerProveedores = tienePermisoCompras(user, verificarAcceso, PERMISOS_COMPRAS.VER_PROVEEDORES_PEDIDO);
  const [data, setData] = useState({});
  const [errors, setErrors] = useState({});
  const [contracted, setContracted] = useState(["presentadas"]);
  const [cotizacionSelection, setCotizacionSelection] = useState([]);
  const [detallesData, setDetallesData] = useState(null);

  const [getPedido, { loading, data: pedido, error: errorPedido }] = useLazyQuery(
    gql`
      query getPedidoCompraModalDetalleLicitacion($id: ID!) {
        pedidosCompra(filter: { id: $id }) {
          id
          tipoCompra
          licitacionEstado
          licitacionInicio
          licitacionFin
          categorias {
            id
            nombre
          }
          cotizaciones {
            id
            createdAt
            file
            proveedor {
              id
              razonSocial
              nombre
              apellido
            }
            detalles {
              id
            }
          }
          detalles {
            id
            nombreServicio
            nombreCompra
            unidadNombre
            unidadAbreviatura
            cantidad
            totalMP
            requierePack
            unidadPack
            materiaPrima {
              id
              nombre
              nombreCompra
              tipo
            }
            cantidadVerificada
            precioEstimado
            cotizaciones {
              id
              precio
              materiaPrimaAlternativo
              nombreAlternativo
              observacion
              plazoEntrega
            }
            cotizacionSeleccionada {
              id
            }
            cotizacionSeleccionadaDesc
          }
        }
      }
    `,
    {
      fetchPolicy: "network-only",
    }
  );
  const [getProveedores, { data: dataProveedores, loading: loadingProveedores }] = useLazyQuery(gql`
    query getProveedores($filter: JSON) {
      proveedores(filter: $filter) {
        id
        razonSocial
        nombre
        apellido
      }
    }
  `);

  const [iniciarLicitacion, { loading: loadingMutation }] = useMutation(
    gql`
      mutation iniciarLicitacion(
        $idPedidoCompra: ID!
        $licitacionInicio: DateTime!
        $licitacionFin: DateTime!
      ) {
        iniciarLicitacion(
          idPedidoCompra: $idPedidoCompra
          licitacionInicio: $licitacionInicio
          licitacionFin: $licitacionFin
        ) {
          id
        }
      }
    `,
    {
      onCompleted: () => {
        refetchParent();
        handleClose();
        handleSnackbar("Proceso de cotización iniciado", "success");
      },
      onError: (error) => {
        handleSnackbar(error.message, "error");
      },
    }
  );

  const [seleccionarCotizaciones, { loading: loadingSeleccion }] = useMutation(
    gql`
      mutation seleccionarCotizaciones(
        $idPedidoCompra: ID!
        $detalleSeleccion: [SeleccionCotizacionInput]!
      ) {
        seleccionarCotizaciones(idPedidoCompra: $idPedidoCompra, detalleSeleccion: $detalleSeleccion) {
          id
        }
      }
    `,
    {
      onCompleted: () => {
        refetchParent();
        handleClose();
        handleSnackbar("Selección de cotizaciones guardada", "success");
      },
      onError: (error) => {
        handleSnackbar(error.message, "error");
      },
    }
  );

  // El cierre puede ser cualquier día posterior al de apertura.
  const minDate = useMemo(() => {
    return (data?.licitacionInicio ? dayjs(data?.licitacionInicio) : dayjs())
      .add(1, "day")
      .startOf("day")
      .format("YYYY-MM-DDTHH:mm");
  }, [data?.licitacionInicio]);

  useEffect(() => {
    if (open && idPedidoCompra) {
      getPedido({ variables: { id: idPedidoCompra } }).then((data) => {
        let pedido = data?.data?.pedidosCompra?.[0];
        if (pedido) {
          // Obtener proveedores que tengan al menos una categoria de las categorias del pedido
          let idsCategorias = data?.data?.pedidosCompra?.[0]?.categorias?.map(
            (categoria) => categoria.id
          );
          getProveedores({
            variables: {
              filter: {
                idsCategorias: idsCategorias,
              },
            },
          });

          // Setear las fechas de inicio y fin de la licitacion
          setData({
            estado: pedido.licitacionEstado,
            licitacionInicio: formatDate(pedido.licitacionInicio, "yyyy-MM-dd hh:mm"),
            licitacionFin: formatDate(pedido.licitacionFin, "yyyy-MM-dd hh:mm"),
          });

          setErrors({
            licitacionFin: { message: "Requerido", visible: false },
          });

          let detalles = [];

          let selection_exists = pedido.detalles.some((d) => Boolean(d.cotizacionSeleccionada));

          if (selection_exists) {
            detalles = pedido.detalles.map((d) => ({
              ...d,
              cotizacion:
                (d.cotizacionSeleccionada &&
                  d.cotizaciones.find((c) => c.id == d.cotizacionSeleccionada.id)) ||
                {},
            }));
          } else if (pedido.licitacionEstado == "Cerrada") {
            detalles = pedido.detalles
              .filter((d) => d.cotizaciones?.length > 0)
              .map((detalle) => {
                return {
                  ...detalle,
                  cotizacion: detalle.cotizaciones.reduce(
                    (prev, current) => (prev.precio < current.precio ? prev : current),
                    {}
                  ),
                };
              });
          }

          setCotizacionSelection(
            detalles.map((detalle) => ({
              detalle: detalle,
              cotizacion: detalle.cotizacion,
              observacion: detalle.cotizacionSeleccionadaDesc,
            }))
          );
        }
      });
    } else {
      setData({});
      setErrors({
        licitacionInicio: { message: "Requerido", visible: false },
        licitacionFin: { message: "Requerido", visible: false },
      });
    }
  }, [idPedidoCompra, open]);



  const handleChange = (e) => {
    const { name, value } = e.target;
    validate(name, value);
    setData((data) => ({ ...data, [name]: value.replace("T", " ") }));
  };

  const handleEnviar = () => {
    confirmar({
      title: "Enviar fechas de cotización",
      message: "¿Desea guardar e informar las fechas de apertura y cierre de cotización a los proveedores?",
      func: () => {
        let input = {};
        input.idPedidoCompra = idPedidoCompra;
        input.licitacionInicio = `${data.licitacionInicio}:00`;
        input.licitacionFin = `${data.licitacionFin}:00`;
        iniciarLicitacion({ variables: input });
      },
    });
  };

  const handleFinalizar = () => {
    if (!puedePreadjudicar) return;
    // Verificar cotizaciones con porcentaje de diferencia > 30%
    let cotizacionesConDiferencia = [];
    const cotizacionesSeleccionadas = cotizacionSelection.filter((c) => c.cotizacion?.id); // Solo considerar las que tienen cotización seleccionada
    cotizacionesSeleccionadas.forEach((selection) => {
      const detalle = selection.detalle;
      const cotizacion = selection.cotizacion;
      const precioEstimado = detalle.precioEstimado || 0;
      const precioCotizado = cotizacion.precio || 0;
      let porcentajeDiferencia = 0;
      if (precioEstimado !== 0) {
        porcentajeDiferencia = ((precioCotizado - precioEstimado) / precioEstimado) * 100;
      }
      // Si el porcentaje supera el 30% (mayor o menor) y no tiene observación
      if (Math.abs(porcentajeDiferencia) > 30 && (!selection.observacion || selection.observacion.trim() === "")) {
        cotizacionesConDiferencia.push({
          ...selection,
          porcentajeDiferencia,
          proveedor: pedido?.pedidosCompra?.[0]?.cotizaciones.find((c) =>
            c.detalles.some((d) => d.id == cotizacion.id)
          )?.proveedor
        });
      }
    });

    // Verificar ítems sin cotización seleccionada y sin observación
    let sinCotizacion = cotizacionSelection.filter((c) => !c.cotizacion?.id && (!c.observacion || c.observacion.trim() === ""));

    // Si hay cotizaciones con diferencia > 30% o ítems sin cotización sin motivo, solicitar motivos
    if (cotizacionesConDiferencia.length > 0 || sinCotizacion.length > 0) {
      // Unir ambos arrays para pedir motivos secuencialmente
      const itemsAProcesar = [...cotizacionesConDiferencia, ...sinCotizacion.map((item) => ({ ...item, sinCotizacion: true }))];
      procesarSecuencialmente(itemsAProcesar)
        .then((datosActualizados) => {
          ejecutarGuardado(datosActualizados);
        })
        .catch((err) => {
          handleSnackbar("Proceso cancelado o error al obtener motivos.", "warning");
        });
    } else {
      // Si no hay cotizaciones con diferencia > 30% ni ítems sin cotización sin motivo, proceder a guardar con los datos actuales
      ejecutarGuardado(cotizacionSelection);
    }
  };

  // const procesarSecuencialmente = async (cotizaciones) => {
  //   // Creamos una copia del estado actual para trabajar
  //   let datosActualizados = [...cotizacionSelection];

  //   for (let i = 0; i < cotizaciones.length; i++) {
  //     const cotizacionActual = cotizaciones[i];
  //     const proveedor = [
  //       cotizacionActual.proveedor?.razonSocial,
  //       cotizacionActual.proveedor?.nombre,
  //       cotizacionActual.proveedor?.apellido,
  //     ].filter(Boolean).join(" ");
  //     const mp = cotizacionActual.detalle?.materiaPrima?.nombre || cotizacionActual.detalle?.nombreServicio || 'Producto/Servicio';
  //     const diferencia = cotizacionActual.porcentajeDiferencia > 0 ? "+" : "";
  //     const porcentajeFormateado = cotizacionActual.porcentajeDiferencia.toFixed(2);
  //     try {
  //       // Esperar a que el usuario ingrese el motivo para esta cotización
  //       const motivo = await new Promise((resolve, reject) => {
  //         confirmar({
  //           title: "Motivo de elección",
  //           message: `La cotización de "${proveedor}" para "${mp}" tiene una diferencia de ${diferencia}${porcentajeFormateado}% respecto al precio estimado. Ingrese motivo de elección:`,
  //           pedirMotivo: true,
  //           placeholder: "Ingrese motivo de elección",
  //           func: (motivoIngresado) => {
  //             resolve(motivoIngresado);
  //           },
  //           onCancel: () => {
  //             // Rechazamos la promesa para detener el proceso
  //             reject(new Error("Usuario canceló"));
  //           }
  //         });
  //       });

  //       // Actualizamos nuestra copia local de los datos
  //       const itemIndex = datosActualizados.findIndex(item => item.detalle.id === cotizacionActual.detalle.id);
  //       if (itemIndex !== -1) {
  //         datosActualizados[itemIndex] = {
  //           ...datosActualizados[itemIndex],
  //           observacion: motivo
  //         };
  //       } else {
  //         console.warn("No se encontró el elemento para actualizar el motivo:", cotizacionActual.detalle.id);
  //       }

  //     } catch (error) {
  //       console.error("Error procesando cotización:", cotizacionActual.detalle.id, error);
  //       // Detenemos el proceso si hay un error o cancelación
  //       throw error;
  //     }
  //   }

  //   // Si llegamos aquí, todas las cotizaciones fueron procesadas y `datosActualizados` tiene todo
  //   return datosActualizados; // Retornamos los datos actualizados
  // };

  // Modificamos ejecutarGuardado para aceptar los datos como parámetro

  const procesarSecuencialmente = async (cotizaciones) => {
    // Creamos una copia del estado actual para trabajar
    let datosActualizados = [...cotizacionSelection];

    for (let i = 0; i < cotizaciones.length; i++) {
      const cotizacionActual = cotizaciones[i];
      const proveedor = [
        cotizacionActual.proveedor?.razonSocial,
        cotizacionActual.proveedor?.nombre,
        cotizacionActual.proveedor?.apellido,
      ].filter(Boolean).join(" ");
      const mp = nombreCompraDetalle(cotizacionActual.detalle);
      const diferencia = cotizacionActual.porcentajeDiferencia > 0 ? "+" : "";
      const porcentajeFormateado = cotizacionActual?.porcentajeDiferencia?.toFixed(2);
      const keys = Object.keys(cotizacionActual?.cotizacion || {});
      try {
        // Esperar a que el usuario ingrese el motivo para esta cotización
        const motivo = await new Promise((resolve, reject) => {
          confirmar({
            title: "Motivo de elección",
            message: keys.length > 0 ? `La cotización de "${proveedor}" para "${mp}" tiene una diferencia de ${diferencia}${porcentajeFormateado}% respecto al precio estimado. Ingrese motivo de elección:` : `Producto ${mp} sin cotización.`,
            pedirMotivo: keys.length > 0,
            placeholder: "Ingrese motivo de elección",
            func: (motivoIngresado) => {
              resolve(keys.length > 0 ? motivoIngresado : "Producto sin cotización");
            },
            onCancel: () => {
              // Rechazamos la promesa para detener el proceso
              reject(new Error("Usuario canceló"));
            }
          });
        });

        // Actualizamos nuestra copia local de los datos
        const itemIndex = datosActualizados.findIndex(item => item.detalle.id === cotizacionActual.detalle.id);
        if (itemIndex !== -1) {
          datosActualizados[itemIndex] = {
            ...datosActualizados[itemIndex],
            observacion: motivo
          };
        } else {
          console.warn("No se encontró el elemento para actualizar el motivo:", cotizacionActual.detalle.id);
        }

      } catch (error) {
        console.error("Error procesando cotización:", cotizacionActual.detalle.id, error);
        // Detenemos el proceso si hay un error o cancelación
        throw error;
      }
    }

    // Si llegamos aquí, todas las cotizaciones fueron procesadas y `datosActualizados` tiene todo
    return datosActualizados; // Retornamos los datos actualizados
  };

  const ejecutarGuardado = (selectionData) => {
    ejecutarGuardadoConDatos(selectionData);
  };

  // Esta función ya estaba bien, la dejamos como está, solo la llamamos con los datos correctos
  const ejecutarGuardadoConDatos = (selectionData) => {
    let input = {};
    input.idPedidoCompra = idPedidoCompra;
    input.detalleSeleccion = selectionData
      .map((c) => ({
        idCotizacionDetalle: c.cotizacion?.id,
        idPedidoCompraDetalle: c.detalle.id,
        observacion: c.observacion,
      }));
    seleccionarCotizaciones({ variables: input });
  };

  const validate = (name, value) => {
    let error = null;
    switch (name) {
      case "licitacionInicio":
        if (!value) {
          error = {
            message: "Requerido",
            visible: false,
          };
          break;
        }
        if (value < formatDate(new Date(), "yyyy-MM-ddThh:mm")) {
          error = {
            message: "La fecha debe ser mayor o igual a hoy",
            visible: true,
          };
          break;
        }
        validate("licitacionFin", data?.licitacionFin);
        break;
      case "licitacionFin":
        if (!value) {
          error = {
            message: "Requerido",
            visible: false,
          };
          break;
        }
        if (value < minDate) {
          error = {
            message: "El cierre debe ser un día posterior al de apertura",
            visible: true,
          };
          break;
        }
        break;
      default:
        break;
    }
    if (error) {
      setErrors((prev) => ({ ...prev, [name]: error }));
    } else if (errors[name]) {
      let err = { ...errors };
      delete err[name];
      setErrors(err);
    }
  };

  const downloadProveedores = () => {
    let proveedores = dataProveedores?.proveedores || [];
    let wb = XLSX.utils.book_new();
    let ws = XLSX.utils.json_to_sheet(proveedores);
    XLSX.utils.book_append_sheet(wb, ws, "Proveedores");
    XLSX.writeFile(wb, `Proveedores_${formatDate(new Date(), "yyyy-MM-dd")}.xlsx`);
  };

  const getIcon = (tipo) => {
    let icon = {};
    switch (tipo) {
      case "Material":
        icon = { name: "category", color: "uproYellow" };
        break;
      case "Consumible":
        icon = { name: "recycling", color: "uproGreen" };
        break;
      default:
        icon = { name: "layers", color: "uproPink" };
    }

    return (
      <Icon fontSize="medium" sx={{ color: getUproColor(icon.color) }}>
        {icon.name}
      </Icon>
    );
  };

  const contract = (name) => {
    if (contracted.some((c) => c == name)) {
      setContracted((prev) => prev.filter((c) => c != name));
    } else {
      setContracted((prev) => prev.concat(name));
    }
  };
  const handleCheck = (e, cotizacion, detalle) => {
    if (data.estado !== "Cerrada") return;
    if (!puedePreadjudicar) return;
    if (cotizacionSelection.some((c) => c.cotizacion?.id == cotizacion.id)) {
      confirmar({
        title: "Motivo de deselección",
        message: "Debe ingresar un motivo de deselección",
        pedirMotivo: true,
        placeholder: "Ingrese motivo",
        func: (motivo) => {
          setCotizacionSelection((prev) =>
            prev.filter((c) => c.cotizacion?.id != cotizacion.id).concat({
              detalle: detalle,
              cotizacion: null,
              observacion: motivo,
            })
          );
        }
      });
      return;
    };

    //get cotizacion mas barata para ese detalle
    let mejorCotizacion = detalle.cotizaciones.reduce(
      (prev, current) => (prev.precio < current.precio ? prev : current),
      {}
    );
    if (cotizacion.id != mejorCotizacion.id) {
      confirmar({
        title: "Motivo de elección",
        message: "La cotización seleccionada no es la más barata, ingrese motivo de elección",
        pedirMotivo: true,
        placeholder: "Ingrese motivo",
        func: (motivo) => {
          setCotizacionSelection((prev) =>
            prev
              .filter((c) => c.detalle.id != detalle.id)
              .concat({
                detalle: detalle,
                cotizacion: cotizacion,
                observacion: motivo,
              })
          );
        },
      });
    } else {
      setCotizacionSelection((prev) =>
        prev
          .filter((c) => c.detalle.id != detalle.id)
          .concat({
            detalle: detalle,
            cotizacion: cotizacion,
            observacion: null,
          })
      );
    }
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ zIndex: 99 }}
          >
            <SoftBox display="flex" gap={1}>
              <Icon fontSize="small">flag</Icon>
              <SoftTypography variant="h6">
                {data.estado === "No iniciada"
                  ? "Iniciar proceso de cotización"
                  : "Detalles de la cotización"}
              </SoftTypography>
            </SoftBox>
            <SoftBox display="flex" justifyContent="end" alignItems="center" gap={1}>
              <Tooltip title="Cerrar" placement="top">
                <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                  close
                </Icon>
              </Tooltip>
            </SoftBox>
          </SoftBox>
          <SoftBox>
            <SoftTypography variant="body2" color="uproYellow" fontWeight="bold">
              {data.estado}
            </SoftTypography>
          </SoftBox>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={4.5}>
              <InputLabel htmlFor="licitacionInicio">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Fecha de inicio
                </SoftTypography>
              </InputLabel>
              <Tooltip title={errors["licitacionInicio"]?.message || ""}>
                <SoftInput
                  type="datetime-local"
                  name="licitacionInicio"
                  disabled={data.estado !== "No iniciada" || !puedeGestionar}
                  error={errors["licitacionInicio"]?.visible}
                  onChange={handleChange}
                  value={data?.licitacionInicio || ""}
                />
              </Tooltip>
            </Grid>
            <Grid item xs={12} lg={4.5}>
              <InputLabel htmlFor="licitacionFin">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Fecha de finalizacion
                </SoftTypography>
              </InputLabel>
              <Tooltip title={errors["licitacionFin"]?.message || ""}>
                <SoftInput
                  type="datetime-local"
                  name="licitacionFin"
                  disabled={data.estado === "Finalizada" || !puedeGestionar}
                  error={errors["licitacionFin"]?.visible}
                  onChange={handleChange}
                  value={data?.licitacionFin || ""}
                  inputProps={{
                    min: minDate,
                  }}
                />
              </Tooltip>
            </Grid>
            {puedeGestionar && (
              <Grid item xs={12} lg={3}>
                <SoftButton
                  color="uproGreen"
                  fullWidth
                  circular
                  sx={{ marginTop: 3.5 }}
                  onClick={handleEnviar}
                  disabled={!pedido?.pedidosCompra?.[0] || data.estado === "Finalizada" || loading || loadingMutation || Object.keys(errors).length > 0}
                >
                  {loadingMutation ? (
                    <CircularProgress color="white" size={17} />
                  ) : (
                    <>Enviar &nbsp; <Icon>send</Icon></>
                  )}
                </SoftButton>
              </Grid>
            )}
          </Grid>

          <SoftBox>
            {errorPedido && <SoftTypography variant="caption" color="error">No se pudo cargar el proceso. Cierre esta ventana y vuelva a abrirla para reintentar.</SoftTypography>}
          </SoftBox>

          <Grid container>
            <Grid item xs={12} md={6}>
              <SoftTypography variant="caption" fontWeight="bold">
                Rubros
              </SoftTypography>
              {pedido?.pedidosCompra?.[0]?.categorias?.map((categoria, index) => (
                <SoftBox key={index}>
                  <SoftTypography variant="body2">- {categoria.nombre}</SoftTypography>
                </SoftBox>
              ))}
            </Grid>
            <Grid item xs={12} md={6}>
              <SoftTypography variant="caption" fontWeight="bold">
                Proveedores
              </SoftTypography>
              <SoftBox display="flex" alignItems="center" gap={1}>
                <SoftTypography variant="body2">
                  {loadingProveedores || !dataProveedores ? 'Cargando' : dataProveedores.proveedores.length}&nbsp; Proveedor
                  {dataProveedores?.proveedores?.length == 1 ? "" : "es"}&nbsp; (en rubros
                  seleccionados)
                </SoftTypography>
                {puedeVerProveedores && (
                  <Tooltip title="Descargar" placement="top">
                    <Icon
                      fontSize="small"
                      sx={{ color: getUproColor("uproGreen"), cursor: "pointer" }}
                      onClick={downloadProveedores}
                    >
                      download
                    </Icon>
                  </Tooltip>
                )}
              </SoftBox>
            </Grid>
            {data.estado !== "No iniciada" && (
              <Grid item xs={12}>
                <SoftBox display="flex" alignItems="center" py={1} sx={{ cursor: "pointer" }}>
                  <SoftTypography
                    variant="caption"
                    fontWeight="bold"
                    onClick={() => contract("presentadas")}
                  >
                    {pedido?.pedidosCompra?.[0]?.licitacionEstado === 'En curso' || pedido?.pedidosCompra?.[0]?.licitacionEstado === 'Programada'
                      ? 'Ofertas selladas: se muestran después del cierre'
                      : !puedeVerOfertas ? 'No tiene permiso para consultar las ofertas'
                      : `Cotizaciones presentadas (${pedido?.pedidosCompra?.[0]?.cotizaciones.length || 0})`}
                  </SoftTypography>
                  <Icon color="secondary">
                    {contracted.some((c) => c == `presentadas`)
                      ? "keyboard_arrow_down"
                      : "keyboard_arrow_up"}
                  </Icon>
                </SoftBox>
                <Collapse in={!contracted.some((c) => c == `presentadas`)}>
                  {puedeVerOfertas && ["Finalizada", "Cerrada"].includes(data.estado) ? (
                    <SoftBox bgColor="grey-100" borderRadius="0.5rem">
                      {pedido?.pedidosCompra?.[0]?.cotizaciones.map((item, index) => (
                        <SoftBox
                          key={index}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          borderBottom={
                            index < pedido?.pedidosCompra?.[0]?.cotizaciones.length - 1
                              ? "1px solid #e6e6e6"
                              : "none"
                          }
                          p={1}
                        >
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <Icon fontSize="small">person</Icon>
                            <SoftTypography variant="caption" fontWeight="bold">
                              {[
                                item?.proveedor.razonSocial,
                                item?.proveedor.nombre,
                                item?.proveedor.apellido,
                              ].join(" ")}
                            </SoftTypography>
                            {item.file && (
                              <SoftButton
                                variant="text"
                                color="uproGreen"
                                onClick={() => {
                                  window.open(resolveStoredFileUrl(item.file), "_blank");
                                }}
                              >
                                Ver archivo
                                <Icon>attach_file</Icon>
                              </SoftButton>
                            )}
                          </SoftBox>
                          <SoftTypography variant="caption">
                            {formatDate(item.createdAt, "dd/MM/yyyy hh:mm")}
                          </SoftTypography>
                          <SoftTypography variant="caption">
                            {item.detalles?.length} producto
                            {item.detalles?.length == 1 ? "" : "s"}
                          </SoftTypography>
                        </SoftBox>
                      ))}
                    </SoftBox>
                  ) : (
                    <SoftBox display="flex" justifyContent="center" alignItems="center" gap={1}>
                      <Icon color="secondary">visibility_off</Icon>
                      <SoftTypography variant="caption" color="secondary">
                        {!puedeVerOfertas ? "No tiene permiso para consultar las ofertas" : "Las ofertas se muestran después del cierre"}
                      </SoftTypography>
                    </SoftBox>
                  )}
                </Collapse>
              </Grid>
            )}
          </Grid>

          {puedeVerOfertas && ["Finalizada", "Cerrada"].includes(data.estado) && (
            <SoftBox mt={2}>
              <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <SoftBox display="flex" alignItems="center" gap={0.5}>
                  <Icon>checklist</Icon>
                  <SoftTypography variant="h6" fontWeight="bold">
                    Preadjudicación
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
              <SoftBox
                bgColor="grey-100"
                borderRadius="0.5rem"
                maxHeight="25vh"
                sx={{ overflowY: "scroll" }}
              >
                {pedido?.pedidosCompra?.[0]?.detalles.map((item, index) => {
                  let observacion = cotizacionSelection.find((s) => s.detalle.id == item.id && !s.cotizacion?.id)
                    ?.observacion;
                  return (
                    <SoftBox key={index}>
                      <Grid
                        container
                        p={1}
                        onClick={() => contract(`detalle-${item.id}`)}
                        sx={{ cursor: "pointer" }}
                        borderTop={index ? "1px solid #e6e6e6" : "none"}
                      >
                        <Grid item xs={1.5} display="flex" alignItems="center" gap={1}>
                          {getIcon(item.materiaPrima?.tipo)}
                          <SoftTypography variant="caption">
                            {item.materiaPrima?.id || item.id}
                          </SoftTypography>
                        </Grid>
                        <Grid item xs={9.5} display="flex" alignItems="center" gap={1}>
                          <SoftTypography variant="caption" fontWeight="bold">
                            {nombreCompraDetalle(item)}
                          </SoftTypography>
                          {observacion && (
                            <Tooltip title={observacion}>
                              <SoftBox display="flex" alignItems="center">
                                <Icon color="secondary" fontSize="small">
                                  edit_note
                                </Icon>
                                <SoftTypography variant="caption" color="secondary">
                                  {recortarTexto(observacion, 30)}
                                </SoftTypography>
                              </SoftBox>
                            </Tooltip>
                          )}
                        </Grid>
                        <Grid item xs={0.5} textAlign="right">
                          <SoftTypography variant="caption">{index + 1}</SoftTypography>
                        </Grid>
                        <Grid
                          item
                          xs={0.5}
                          display="flex"
                          alignItems="center"
                          justifyContent="flex-end"
                        >
                          <Icon color="secondary">
                            {contracted.some((c) => c == `detalle-${item.id}`)
                              ? "keyboard_arrow_down"
                              : "keyboard_arrow_up"}
                          </Icon>
                        </Grid>
                      </Grid>
                      <Collapse in={!contracted.some((c) => c == `detalle-${item.id}`)}>
                        {item.cotizaciones?.map((cotizacion, j) => {
                          let proveedor = pedido?.pedidosCompra?.[0]?.cotizaciones.find((c) =>
                            c.detalles.some((d) => d.id == cotizacion.id)
                          )?.proveedor;
                          let selection = cotizacionSelection.find(
                            (c) => c.cotizacion?.id == cotizacion.id
                          );
                          let checked = Boolean(selection);
                          let mejorPrecio =
                            item.cotizaciones.reduce(
                              (prev, current) => (prev.precio < current.precio ? prev : current),
                              {}
                            ).id == cotizacion.id;
                          let observacion = selection?.observacion;
                          // Cálculo del porcentaje de diferencia
                          const precioEstimado = item.precioEstimado || 0;
                          const precioCotizado = cotizacion.precio || 0;
                          let porcentajeDiferencia = 0;
                          if (precioEstimado !== 0) {
                            porcentajeDiferencia = ((precioCotizado - precioEstimado) / precioEstimado) * 100;
                          }

                          let color = "secondary";
                          let simbolo = "";
                          if (porcentajeDiferencia > 30) {
                            color = "error";
                            simbolo = "+";
                          } else if (porcentajeDiferencia < -30) {
                            color = "error";
                            simbolo = "-";
                          }
                          return (
                            <SoftBox key={j} bgColor="white">
                              <Grid container p={1} borderTop="1px solid #e6e6e6">
                                <Grid
                                  item
                                  xs={1}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  <Icon
                                    sx={{ cursor: data.estado !== "Cerrada" || !puedePreadjudicar ? "default" : "pointer" }}
                                    onClick={(e) => handleCheck(e, cotizacion, item)}
                                  >
                                    {checked ? "check_box" : "check_box_outline_blank"}
                                  </Icon>
                                </Grid>
                                <Grid item xs={4.5} display="flex" alignItems="center" gap={1}>
                                  <Icon fontSize="small">person</Icon>
                                  <SoftTypography
                                    variant="caption"
                                    fontWeight={checked ? "bold" : "regular"}
                                  >
                                    {[
                                      proveedor?.razonSocial,
                                      proveedor?.nombre,
                                      proveedor?.apellido,
                                    ].join(" ")}
                                  </SoftTypography>
                                  {mejorPrecio && (
                                    <Tooltip title="Mejor precio">
                                      <Icon color="success">keyboard_double_arrow_down</Icon>
                                    </Tooltip>
                                  )}
                                  {cotizacion.materiaPrimaAlternativo && (
                                    <Tooltip title="Materia prima alternativo">
                                      <Icon color="primary">swap_horizontal</Icon>
                                    </Tooltip>
                                  )}
                                  {observacion && (
                                    <Tooltip title={observacion}>
                                      <SoftBox display="flex" alignItems="center">
                                        <Icon color="secondary" fontSize="small">
                                          edit_note
                                        </Icon>
                                        <SoftTypography variant="caption" color="secondary">
                                          {recortarTexto(observacion, 30)}
                                        </SoftTypography>
                                      </SoftBox>
                                    </Tooltip>
                                  )}
                                </Grid>
                                <Grid
                                  item
                                  xs={1.5}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="flex-end"
                                >
                                  <SoftTypography
                                    variant="caption"
                                    fontWeight={checked ? "bold" : "regular"}
                                  >
                                    $ {formatMoneyPunto(cotizacion.precio)} / {item.requierePack ? item.unidadPack : item.unidadAbreviatura}
                                  </SoftTypography>
                                </Grid>
                                <Grid
                                  item
                                  xs={1}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="flex-end"
                                >
                                  <SoftTypography
                                    variant="caption"
                                    fontWeight={checked ? "bold" : "regular"}
                                  >
                                    {item.cantidadVerificada} {item.requierePack ? item.unidadPack : item.unidadAbreviatura}
                                  </SoftTypography>
                                </Grid>
                                <Grid
                                  item
                                  xs={1.5}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="flex-end"
                                >
                                  <SoftTypography
                                    variant="caption"
                                    fontWeight={checked ? "bold" : "regular"}
                                  >
                                    $ {formatMoneyPunto(cotizacion.precio * item.cantidadVerificada)}
                                  </SoftTypography>
                                </Grid>
                                <Grid
                                  item
                                  xs={1.5}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="flex-end"
                                >
                                  <SoftTypography
                                    variant="caption"
                                    fontWeight={checked ? "bold" : "regular"}
                                    color={color}
                                  >
                                    {simbolo}{Math.abs(porcentajeDiferencia).toFixed(2)}%
                                  </SoftTypography>
                                </Grid>
                                <Grid
                                  item
                                  xs={0.5}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="flex-end"
                                >
                                  <SoftTypography
                                    variant="caption"
                                    fontWeight={checked ? "bold" : "regular"}
                                  >
                                    {cotizacion?.plazoEntrega || "N/A"} días
                                  </SoftTypography>
                                </Grid>
                                <Grid
                                  item
                                  xs={0.5}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="flex-end"
                                >
                                  <Tooltip title="Ver detalles">
                                    <Icon
                                      sx={{ cursor: "pointer" }}
                                      onClick={() =>
                                        setDetallesData({ cotizacion, detalle: item, proveedor })
                                      }
                                      color="secondary"
                                    >
                                      info
                                    </Icon>
                                  </Tooltip>
                                </Grid>
                              </Grid>
                            </SoftBox>
                          );
                        })}
                        {item.cotizaciones?.length == 0 && (
                          <SoftBox p={1} display="flex" justifyContent="center" bgColor="white" borderTop="1px solid #e6e6e6">
                            <SoftTypography variant="caption" color="secondary">
                              Sin cotizaciones
                            </SoftTypography>
                          </SoftBox>
                        )}
                      </Collapse>
                    </SoftBox>
                  )
                })}
              </SoftBox>
              <SoftBox bgColor="grey-100" borderRadius="0.5rem" mt={1}>
                <Grid container px={1} py={0.5}>
                  <Grid item xs={6}>
                    <SoftTypography variant="button" fontWeight="bold">
                      Total
                    </SoftTypography>
                  </Grid>
                  <Grid item xs={6} display="flex" alignItems="center" justifyContent="flex-end">
                    <SoftTypography variant="button" fontWeight="bold">
                      ${" "}
                      {formatMoneyPunto(
                        cotizacionSelection
                          .filter((c) => c.cotizacion?.id)
                          .reduce(
                            (prev, current) =>
                              prev + current.cotizacion.precio * current.detalle.cantidadVerificada,
                            0
                          ) || 0
                      )}
                    </SoftTypography>
                  </Grid>
                </Grid>
              </SoftBox>
            </SoftBox>
          )}

          {data.estado === "Cerrada" && puedePreadjudicar && (
            <SoftButton color="uproGreen" circular sx={{ marginTop: 2 }} onClick={handleFinalizar}>
              {loadingSeleccion ? <CircularProgress color="white" size={17} /> : <Icon>save</Icon>}
              &nbsp;Guardar selección
            </SoftButton>
          )}

          <DinamicModal
            open={!!detallesData}
            handleClose={() => setDetallesData(null)}
            title="Detalle de cotización"
            data={{
              Proveedor: [
                detallesData?.proveedor?.razonSocial,
                detallesData?.proveedor?.nombre,
                detallesData?.proveedor?.apellido,
              ].join(" "),
              Producto: detallesData?.cotizacion?.materiaPrimaAlternativo
                ? detallesData?.cotizacion?.nombreAlternativo
                : nombreCompraDetalle(detallesData?.detalle),
              Alternativo: detallesData?.cotizacion?.materiaPrimaAlternativo ? "Si" : "No",
              "Precio unitario": `$ ${formatMoneyPunto(detallesData?.cotizacion?.precio || 0)}`,
              Cantidad: `${detallesData?.detalle?.cantidadVerificada || 0} ${detallesData?.detalle?.requierePack ? detallesData?.detalle?.unidadPack : detallesData?.detalle?.unidadAbreviatura || ""}`,
              "Unidad del precio": detallesData?.detalle?.requierePack ? detallesData?.detalle?.unidadPack : detallesData?.detalle?.unidadNombre,
              Equivalencia: detallesData?.detalle?.requierePack ? `1 ${detallesData?.detalle?.unidadPack} = ${Number(detallesData?.detalle?.totalMP) / Number(detallesData?.detalle?.cantidad)} ${detallesData?.detalle?.unidadAbreviatura || ""}` : "Unidad del catálogo",
              Total: `$ ${formatMoneyPunto(
                detallesData?.cotizacion?.precio * detallesData?.detalle?.cantidadVerificada || 0
              )}`,
              "Plazo de Entrega": detallesData?.cotizacion?.plazoEntrega + " " + "Días Corridos",
              Observación: detallesData?.cotizacion?.observacion || "Sin observaciones",
            }}
          />
        </Card>
      </Fade>
    </Modal>
  );
}

ModalDetalleLicitacion.defaultProps = {
  refetchParent: () => { },
  mode: "Lectura",
};

ModalDetalleLicitacion.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  idPedidoCompra: PropTypes.string,
  refetchParent: PropTypes.func,
  mode: PropTypes.oneOf(["Compras", "Solicitante", "Lectura"]),
};
